import { inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { PurlyService } from './purly.service';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Angular APP_INITIALIZER factory function for Purly service initialization.
 *
 * This initializer ensures that the Purly service is initialized during
 * application startup, before the main application components are loaded.
 * It acts as a startup gate for basemap and tile service URL discovery.
 *
 * INITIALIZATION ORDER:
 * 1. loadRuntimeConfig() runs first and loads assets/runtime-config.json into RuntimeConfigService
 * 2. This initializer runs second and reads from RuntimeConfigService
 *
 * CONFIGURATION:
 * Purly configuration MUST be provided via assets/runtime-config.json.
 * The runtime config loader will fetch this file during app initialization.
 * If the file is missing or invalid, Purly initialization will be skipped.
 *
 * @returns Factory function that returns a Promise for Angular's APP_INITIALIZER
 */
export function initializePurlyService() {
  // Capture dependencies immediately in injection context
  let purlyService: PurlyService;
  let runtimeConfigService: RuntimeConfigService;
  let logger: NGXLogger;

  try {
    purlyService = inject(PurlyService);
    runtimeConfigService = inject(RuntimeConfigService);
    logger = inject(NGXLogger);
  } catch (error) {
    console.error('[PurlyInitializer] ❌ CRITICAL: Failed to inject dependencies:', error);
    throw error;
  }

  return async (): Promise<void> => {
    logger.info('[PurlyInitializer] Starting Purly service initialization');

    // Get Purly config from RuntimeConfigService
    // Config is loaded by loadRuntimeConfig() APP_INITIALIZER from assets/runtime-config.json
    if (!runtimeConfigService.isConfigLoaded()) {
      logger.warn(
        '[PurlyInitializer] No Purly configuration found, skipping initialization'
      );
      logger.info('[PurlyInitializer] Ensure assets/runtime-config.json exists and is valid');
      return;
    }

    const purlyConfig = runtimeConfigService.getPurlyConfig();

    // Check if Purly service is enabled
    if (purlyConfig.enabled === false) {
      logger.info('[PurlyInitializer] Purly service disabled in configuration');
      return;
    }

    try {
      const startTime = performance.now();
      const result = await purlyService.initialize(purlyConfig);
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      if (result.success) {
        logger.info('[PurlyInitializer] Initialization completed successfully', {
          duration: `${duration}ms`,
          urlCount: result.configuration?.root?.length || 0,
          basemaps: Object.keys(result.signedUrls?.basemaps || {}).length,
          tileServices: Object.keys(result.signedUrls?.tileServices || {})
            .length
        });
      } else {
        logger.error('[PurlyInitializer] Initialization failed', {
          duration: `${duration}ms`,
          error: result.error
        });

        // Don't throw here - allow app to continue with degraded functionality
        // The PurlyService will return empty arrays for signed URLs
      }
    } catch (error) {
      logger.error('[PurlyInitializer] Unexpected error during initialization', {
        error
      });

      // Don't throw - allow app to continue running
      // The service will handle the error state internally
    }
  };
}
